import type { QRCode } from 'jsqr';
import {
  animationFrameScheduler,
  filter,
  from,
  lastValueFrom,
  type Observable,
  of as observableOf,
  repeat,
  Subject,
  Subscription,
  subscribeOn,
  switchMap,
  takeWhile,
  withLatestFrom,
} from 'rxjs';
import { EsThread, Transfer } from 'threads-es';
import type { CodeScannerWorkerAPI } from '@/assets/workers/qr.worker';
import CodeScannerWorker from '@/assets/workers/qr.worker?worker';
import { createLogger } from '@/utils/logger';

export function createCodeScanner() {
  return new CodeScanner();
}

const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: 'environment',
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 15 },
  },
};

export default class CodeScanner {
  readonly #logger = createLogger('Code Scanner');
  readonly #worker$ = from(CodeScanner.#createWorker());
  readonly #event$ = new Subject<QRCode>();

  #state: 'idle' | 'scanning' | 'scanned' = 'idle';
  #stream: MediaStream | null = null;
  #subscriptions$$ = new Subscription();

  async startScanner(videoEl: HTMLVideoElement) {
    if (this.#state !== 'idle') return;

    this.#logger.info('Start scanner');
    this.#state = 'scanning';

    await this.#startVideo(videoEl);

    const subscription$$ = observableOf(videoEl)
      .pipe(
        subscribeOn(animationFrameScheduler),
        filter((video) => video.readyState === video.HAVE_ENOUGH_DATA),
        takeWhile(() => this.#state === 'scanning'),
        switchMap((video) =>
          from(loadMediaAsImageData(video, video.videoWidth, video.videoHeight)).pipe(
            withLatestFrom(this.#worker$),
            switchMap(([imageData, worker]) =>
              from(worker.methods.process(Transfer(imageData.data.buffer), imageData.width, imageData.height)),
            ),
          ),
        ),
        repeat({ delay: 300 }),
        filter((result) => result !== null),
      )
      .subscribe({
        next: (result) => {
          this.#logger.info('Code found', result.data);
          this.#event$.next(result);
          this.#state = 'scanned';
        },
        error: (error) => {
          this.#logger.fatal(error);
        },
      });

    this.#subscriptions$$.add(subscription$$);
  }

  stopScanner() {
    if (this.#state === 'idle') return;

    this.#logger.info('Stop scanner');
    this.#state = 'idle';

    this.#stream?.getTracks().forEach((track) => track.stop());
    this.#subscriptions$$.unsubscribe();
    this.#subscriptions$$ = new Subscription();
  }

  async scanFile(file: File): Promise<QRCode | null> {
    const image = await loadImageAsImageData(file);

    const worker = await lastValueFrom(this.#worker$);
    const result = await worker.methods.process(Transfer(image.data.buffer), image.width, image.height);

    if (result) {
      this.#logger.info('Code found', result.data);
      this.#event$.next(result);
      this.#state = 'scanned';
    } else {
      this.#logger.info('Code not found');
    }

    return result;
  }

  get event$(): Observable<QRCode> {
    return this.#event$;
  }

  async #startVideo(videoEl: HTMLVideoElement) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
      videoEl.srcObject = this.#stream = stream;

      return stream;
    } catch (err) {
      this.#logger.error('Failed to access user media', err);
      throw err;
    }
  }

  static #createWorker() {
    return EsThread.Spawn<CodeScannerWorkerAPI>(new CodeScannerWorker({ name: 'code-scanner-worker' }), {
      timeout: 15_000,
    });
  }
}

async function loadImageAsImageData(file: File): Promise<ImageData> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = (err) => {
      reject(err);
    };

    reader.readAsDataURL(file);
  });

  return loadMediaAsImageData(image, image.width, image.height);
}

export function loadMediaAsImageData(mediaEl: HTMLImageElement | HTMLVideoElement, width: number, height: number) {
  return new Promise<ImageData>((resolve, reject) => {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');

    if (context) {
      context.drawImage(mediaEl, 0, 0, width, height);

      resolve(context.getImageData(0, 0, width, height));
    } else {
      reject('Failed to create canvas context');
    }
  });
}
